import { customElement } from 'lit/decorators.js';
import { FabricationJobState, type FabricationJobSummaryView } from '../../../api/fabrication-job-api';
import { getApiFactory } from '../../../api/api-injector';
import {
  FabricationJobSummaryTable,
  type FabricationJobSummaryTableOptions
} from '../../../fabrication-jobs/views/fabrication-job-summary-table';
import { html } from 'lit';
import { LangFabricationJobPicker } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-picker-lang';
import { LangFabricationJobSummaryTable } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-summary-table-lang';
import {
  ModalPickerDialog,
  type ModalPickerDialogOptions
} from '../../../../webmodule-common/other/ui/modal-picker-base';
import { newGuid } from '../../../../webmodule-common/other/api/guid';
import type { NullPromise } from '../../../../webmodule-common/other/ui/events';
import type {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';

interface FabricationJobPickerBrowserOptions extends FabricationJobSummaryTableOptions {
  onJobSelect?: (job: FabricationJobSummaryView) => void;
}

@customElement('wm-fabricationjobpickerbrowser')
class FabricationJobPickerBrowser extends FabricationJobSummaryTable {
  onJobSelect?: (job: FabricationJobSummaryView) => void;

  constructor(options: FabricationJobPickerBrowserOptions) {
    super(options);
    this.onJobSelect = options.onJobSelect;
    this.clickrows = true;
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  override getColumns(): WebModuleLitTableColumnDef[] {
    return [
      {
        title: () => LangFabricationJobSummaryTable.jobNumberTitle(),
        fieldName: 'jobNumber',
        sortable: true,
        classes: 'colpx-70 fabrication-job-number',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return html`${job.jobNumber}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.titleTitle(),
        fieldName: 'title',
        sortable: true,
        classes: 'colpx-150 fabrication-job-title',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return html`${job.title || LangFabricationJobSummaryTable.untitled()}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.itemsTitle(),
        fieldName: 'itemCount',
        classes: 'colpx-50 fabrication-job-items no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return html`${job.itemCount}`;
        }
      }
    ];
  }

  override async rowClicked(
    e: CustomEvent<{ table: WebModuleLitTable; item: FabricationJobSummaryView }>
  ): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    this.eventHandler(async () => {
      this.onJobSelect?.(e.detail.item);
    });
  }
}

interface FabricationJobPickerOptions extends ModalPickerDialogOptions<FabricationJobSummaryView> {
  branchId: string;
}

class FabricationJobBrowserPicker extends ModalPickerDialog<FabricationJobSummaryView> {
  private browser: FabricationJobPickerBrowser;
  private branchId: string;

  constructor(options: FabricationJobPickerOptions) {
    super(options);
    this.branchId = options.branchId;

    this.browser = new FabricationJobPickerBrowser({
      title: () => options.title ?? '',
      branchId: async () => options.branchId,
      states: [FabricationJobState.Draft],
      pageFragment: '',
      onJobSelect: job => this.select(job)
    });
  }

  protected get modalSize(): string {
    return 'modal-lg';
  }

  bodyTemplate() {
    return html`${this.browser}`;
  }

  footerTemplate() {
    return html`
      <webmodule-button size="small" variant="default" @click=${() => this.createNewJob()}>
        ${LangFabricationJobPicker.createNewJob()}
      </webmodule-button>
      <webmodule-button size="small" variant="default" @click=${() => this.cancel()}>
        ${LangFabricationJobPicker.cancel()}
      </webmodule-button>
    `;
  }

  private async createNewJob(): Promise<void> {
    const api = getApiFactory().fabricationJob();
    const id = newGuid();
    const result = await api.createFabricationJob({
      id,
      branchId: this.branchId,
      title: '',
      description: ''
    });
    if (result) {
      const job = result.fabricationJob;
      await this.select({
        id: job.id,
        recordVersion: job.recordVersion,
        jobNumber: job.jobNumber,
        title: job.title,
        state: job.state,
        itemCount: 0,
        distinctQuoteItemIds: [],
        dateCreated: job.dateCreated,
        lastModifiedDate: job.lastModifiedDate
      });
    }
  }
}

export async function fabricationJobPicker(branchId: string): NullPromise<FabricationJobSummaryView> {
  return new Promise<FabricationJobSummaryView | null>(resolve => {
    const picker = new FabricationJobBrowserPicker({
      onSelect: (selected: FabricationJobSummaryView | null) => {
        resolve(selected);
      },
      onCancel: () => resolve(null),
      title: LangFabricationJobPicker.selectFabricationJob(),
      branchId
    });
    picker.showModal().then(() => resolve(null));
  });
}
